/**
 * OmegaMonitorPage — OMEGA Self-Health dashboard (Sprint 47).
 *
 * Route: /omega/monitor (founder-only).
 *
 * Shows the Monitor's OMEGA-self use: each operational metric with its current
 * value, learned baseline, a green/yellow/red indicator, and a 5-field human
 * explanation (WHAT / WHY / HOW MANY changed / CONFIDENCE / ACTION). Below, a
 * drift-alerts feed of the C-10 signed self-insights from /admin/insights.
 *
 * Every number is paired with human text — no bare figures.
 * English only. Mobile-responsive from 375px.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/http';
import { cn } from '@/utils/cn';

// ── Wire types ────────────────────────────────────────────────────────────────

interface MetricHealth {
  metric: string;
  value: number;
  baseline_mean: number;
  baseline_std: number;
  samples: number;
  recommended_action: string;
}

interface HealthResponse {
  pillar: string;
  description: string;
  metrics: MetricHealth[];
}

interface InsightPayload {
  action: string;
  target: unknown;
  metric: string;
  delta: { magnitude: number; direction: string; is_anomaly: boolean };
  hypothesis: string;
  confidence: number;
}

interface InsightsResponse {
  total_insights: number;
  insights: InsightPayload[];
}

// ── Per-metric human descriptions (the WHAT + WHY) ────────────────────────────

const METRIC_META: Record<string, { label: string; what: string; why: string }> = {
  knowledge_atoms: {
    label: 'Knowledge Atoms',
    what: 'The total number of tagged knowledge atoms OMEGA has stored (the durable atom_tags corpus — millions, not an in-memory subset).',
    why: 'This is OMEGA’s accumulated knowledge. Steady growth means she is still learning; a sudden stall or drop usually means an ingestion pipeline broke.',
  },
  discovery_outputs_total: {
    label: 'Discovery Outputs',
    what: 'The total number of insights the six discovery labs have produced.',
    why: 'It measures how much OMEGA is discovering. A sharp change means the labs sped up, slowed down, or stopped finding new opportunities.',
  },
  conductors_running: {
    label: 'Active Research Teams',
    what: 'How many research teams are gathering knowledge right now.',
    why: 'It shows live research activity. An abnormal count can mean teams are stuck running, or that none are working at all.',
  },
};

function metaFor(metric: string) {
  return (
    METRIC_META[metric] ?? {
      label: metric,
      what: 'An OMEGA operational metric.',
      why: 'Tracked so OMEGA can notice when her own behaviour drifts.',
    }
  );
}

// ── Assessment (indicator + the changing 5-field values) ──────────────────────

type Status = 'learning' | 'normal' | 'watch' | 'alert';

function assess(m: MetricHealth) {
  const learning = m.samples < 2 || m.baseline_std <= 0;
  const deviation = m.value - m.baseline_mean;
  const sigma = learning ? 0 : Math.abs(deviation) / m.baseline_std;
  let status: Status;
  if (learning) status = 'learning';
  else if (sigma < 1) status = 'normal';
  else if (sigma < 3) status = 'watch';
  else status = 'alert';
  const confidence = m.samples >= 20 ? 'high' : m.samples >= 5 ? 'medium' : 'low';
  return { learning, deviation, sigma, status, confidence };
}

const STATUS_STYLE: Record<Status, { dot: string; chip: string; label: string }> = {
  normal: { dot: 'bg-success', chip: 'border-success/40 bg-success/10 text-success', label: 'Normal' },
  watch: {
    dot: 'bg-amber-500',
    chip: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    label: 'Watch',
  },
  alert: { dot: 'bg-danger', chip: 'border-danger/40 bg-danger/10 text-danger', label: 'Alert' },
  learning: {
    dot: 'bg-ink-subtle',
    chip: 'border-border bg-surface text-ink-subtle',
    label: 'Learning baseline',
  },
};

function fmt(n: number): string {
  return Math.round(n).toLocaleString();
}

// ── Field row: a labelled line of human text ──────────────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p className="text-sm leading-snug text-ink-muted">
      <span className="font-semibold text-ink">{label}: </span>
      {children}
    </p>
  );
}

// ── Metric card ───────────────────────────────────────────────────────────────

function MetricCard({ m }: { m: MetricHealth }) {
  const meta = metaFor(m.metric);
  const a = assess(m);
  const s = STATUS_STYLE[a.status];

  const howMany = a.learning
    ? `Still learning the normal range (${m.samples} of ≥2 readings so far).`
    : `Now ${fmt(m.value)} vs a usual ~${fmt(m.baseline_mean)} — ${a.deviation >= 0 ? 'up' : 'down'} ${fmt(
        Math.abs(a.deviation),
      )} (${a.sigma.toFixed(1)}σ from normal).`;

  const action = a.learning
    ? 'No action yet — OMEGA is still establishing this metric’s baseline.'
    : a.status === 'normal'
      ? 'Within the normal range — no action needed.'
      : m.recommended_action;

  return (
    <div className={cn('flex flex-col gap-3 rounded-xl border bg-canvas p-4', s.chip.split(' ')[0])}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-ink">{meta.label}</h3>
        <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium', s.chip)}>
          <span className={cn('h-2 w-2 rounded-full', s.dot)} />
          {s.label}
        </span>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-3xl font-bold tabular-nums text-ink">{fmt(m.value)}</span>
        <span className="text-xs text-ink-subtle">
          baseline ~{fmt(m.baseline_mean)} {m.baseline_std > 0 ? `(±${fmt(m.baseline_std)})` : ''}
        </span>
      </div>

      <div className="flex flex-col gap-1.5 border-t border-border pt-3">
        <Field label="What">{meta.what}</Field>
        <Field label="Why it matters">{meta.why}</Field>
        <Field label="What changed">{howMany}</Field>
        <Field label="Confidence">
          {a.confidence} (based on {m.samples} reading{m.samples === 1 ? '' : 's'})
        </Field>
        <Field label="Recommended action">{action}</Field>
      </div>
    </div>
  );
}

// ── Drift alert card ──────────────────────────────────────────────────────────

function AlertCard({ insight }: { insight: InsightPayload }) {
  const dir = insight.delta.direction;
  const dirLabel = dir === 'above' ? 'above normal' : dir === 'below' ? 'below normal' : dir;
  const confidencePct = Math.round((insight.confidence ?? 0) * 100);
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">
          {metaFor(insight.metric).label} · {dirLabel}
        </span>
        <span className="text-[11px] text-ink-subtle">confidence {confidencePct}%</span>
      </div>
      {/* The hypothesis is OMEGA's own human-text explanation (what / how many σ / vs baseline / action). */}
      <p className="text-sm leading-snug text-ink">{insight.hypothesis}</p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function OmegaMonitorPage() {
  const health = useQuery({
    queryKey: ['omega', 'self-health'],
    queryFn: () => apiFetch<HealthResponse>('/admin/omega-self/health'),
    refetchInterval: 30_000,
  });

  const insights = useQuery({
    queryKey: ['omega', 'self-insights'],
    queryFn: () => apiFetch<InsightsResponse>('/admin/insights'),
    refetchInterval: 30_000,
  });

  const selfInsights = (insights.data?.insights ?? []).filter((i) => i.target === 'OmegaSelf');

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-6 sm:px-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-ink sm:text-2xl">OMEGA Self-Health</h1>
        <p className="text-sm text-ink-muted">
          OMEGA watches her own behaviour. Each metric below is compared to the normal range she
          has learned; a drift of 3σ or more raises a signed alert. OMEGA never changes herself —
          she only flags what a human should look at.
        </p>
      </header>

      {/* Metrics */}
      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-subtle">Metrics</h2>
        {health.isPending && <p className="text-sm text-ink-muted">Loading self-health…</p>}
        {health.isError && (
          <p className="text-sm text-danger">Failed to load OMEGA self-health.</p>
        )}
        {health.data && health.data.metrics.length === 0 && (
          <p className="text-sm text-ink-muted">No metrics captured yet.</p>
        )}
        {health.data && health.data.metrics.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {health.data.metrics.map((m) => (
              <MetricCard key={m.metric} m={m} />
            ))}
          </div>
        )}
      </section>

      {/* Drift alerts */}
      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-subtle">
          Drift alerts
        </h2>
        {insights.isPending && <p className="text-sm text-ink-muted">Loading alerts…</p>}
        {insights.isError && <p className="text-sm text-danger">Failed to load drift alerts.</p>}
        {!insights.isPending && !insights.isError && selfInsights.length === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-surface p-6 text-center">
            <p className="text-sm text-ink-muted">
              No self-drift alerts. OMEGA’s behaviour is within its normal range — alerts appear
              here when a metric drifts 3σ or more from baseline.
            </p>
          </div>
        )}
        {selfInsights.length > 0 && (
          <div className="flex flex-col gap-3">
            {selfInsights.map((i, idx) => (
              <AlertCard key={`${i.metric}-${idx}`} insight={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default OmegaMonitorPage;
